import React, { useRef } from 'react';
import { Rect, Circle, Star, Line as KonvaLine, RegularPolygon, Arrow, Path, Group } from 'react-konva';
import { observer } from 'mobx-react-lite';

const FigureElementComponent = observer(({ element, isSelected, onSelect, onTransformEnd, onDragEnd, groupChild }) => {
  const shapeRef = useRef(null);

  if (!element.visible) return null;

  const handleDragEnd = (e) => {
    element.set({
      x: e.target.x(),
      y: e.target.y(),
    });
    if (onDragEnd) onDragEnd(e);
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    element.set({
      x: node.x(),
      y: node.y(),
      width: Math.max(5, node.width() * scaleX),
      height: Math.max(5, node.height() * scaleY),
      rotation: node.rotation(),
    });

    if (onTransformEnd) onTransformEnd(e);
  };

  const commonProps = {
    id: element.id,
    name: element.name || 'figure',
    x: element.x,
    y: element.y,
    rotation: element.rotation,
    opacity: element.opacity,
    fill: element.fill,
    stroke: element.stroke || undefined,
    strokeWidth: element.strokeWidth || 0,
    dash: element.dash?.length > 0 ? [...element.dash] : undefined,
    draggable: !groupChild && element.draggable,
    visible: element.visible,
    shadowEnabled: element.shadowEnabled,
    shadowColor: element.shadowColor,
    shadowBlur: element.shadowBlur,
    shadowOffsetX: element.shadowOffsetX,
    shadowOffsetY: element.shadowOffsetY,
    shadowOpacity: element.shadowOpacity,
    onClick: (e) => { if (onSelect) onSelect(e); },
    onTap: (e) => { if (onSelect) onSelect(e); },
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
  };

  const subType = element.subType || 'rect';

  // Center-origin shapes use offset so that node.x()/y() stays top-left
  // (matching our model) while the visual center is positioned correctly.
  const centerOffset = {
    offsetX: -(element.width / 2),
    offsetY: -(element.height / 2),
  };

  switch (subType) {
    case 'circle':
    case 'ellipse':
      return (
        <Circle
          ref={shapeRef}
          {...commonProps}
          {...centerOffset}
          radiusX={element.width / 2}
          radiusY={element.height / 2}
          width={element.width}
          height={element.height}
        />
      );

    case 'triangle':
      return (
        <RegularPolygon
          ref={shapeRef}
          {...commonProps}
          {...centerOffset}
          sides={3}
          radius={Math.min(element.width, element.height) / 2}
          width={element.width}
          height={element.height}
        />
      );

    case 'star':
      return (
        <Star
          ref={shapeRef}
          {...commonProps}
          {...centerOffset}
          numPoints={5}
          innerRadius={Math.min(element.width, element.height) / 4}
          outerRadius={Math.min(element.width, element.height) / 2}
          width={element.width}
          height={element.height}
        />
      );

    case 'polygon':
    case 'hexagon':
      return (
        <RegularPolygon
          ref={shapeRef}
          {...commonProps}
          {...centerOffset}
          sides={subType === 'hexagon' ? 6 : 5}
          radius={Math.min(element.width, element.height) / 2}
          width={element.width}
          height={element.height}
        />
      );

    case 'diamond':
      return (
        <RegularPolygon
          ref={shapeRef}
          {...commonProps}
          {...centerOffset}
          sides={4}
          radius={Math.min(element.width, element.height) / 2}
          width={element.width}
          height={element.height}
        />
      );

    case 'path':
      if (!element.pathData) return null;
      return (
        <Group
          ref={shapeRef}
          {...commonProps}
          width={element.width}
          height={element.height}
        >
          <Path
            data={element.pathData}
            fill={element.fill}
            stroke={element.stroke || undefined}
            strokeWidth={element.strokeWidth ? element.strokeWidth * (100 / element.width) : 0}
            scaleX={element.width / 100}
            scaleY={element.height / 100}
          />
        </Group>
      );

    case 'arrow':
      return (
        <Arrow
          ref={shapeRef}
          {...commonProps}
          points={[0, element.height / 2, element.width, element.height / 2]}
          pointerLength={10}
          pointerWidth={10}
          width={element.width}
          height={element.height}
        />
      );

    case 'rect':
    default:
      return (
        <Rect
          ref={shapeRef}
          {...commonProps}
          width={element.width}
          height={element.height}
          cornerRadius={element.cornerRadius || 0}
        />
      );
  }
});

FigureElementComponent.displayName = 'FigureElement';
export default FigureElementComponent;
